import { useEffect, type CSSProperties } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { User } from "@supabase/supabase-js";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useTheme } from "@/hooks/useTheme";
import { trackEvent } from "@/lib/posthog";
import DashboardLayout, { displayFirstName } from "@/components/dashboard/DashboardLayout";
import {
  HarmonogramyIllustration,
  KosztyIllustration,
  OfertyIllustration,
  UmowyIllustration,
} from "@/components/dashboard/StartPageModuleIllustrations";

type ModuleTileId = "oferty" | "umowy" | "harmonogramy" | "koszty";

type ModuleTile = {
  id: ModuleTileId;
  title: string;
  description: string;
  locked: boolean;
};

const MODULE_TILES: ModuleTile[] = [
  {
    id: "oferty",
    title: "Oferty",
    description: "Twórz i wysyłaj wyceny. Śledź akceptacje klientów.",
    locked: false,
  },
  {
    id: "umowy",
    title: "Umowy",
    description: "Generuj umowy i zlecenia na podstawie oferty.",
    locked: true,
  },
  {
    id: "harmonogramy",
    title: "Harmonogramy",
    description: "Planuj etapy robót i informuj klientów o postępach.",
    locked: true,
  },
  {
    id: "koszty",
    title: "Koszty",
    description: "Zbieraj paragony i faktury. Licz ile wydałeś na projekt.",
    locked: true,
  },
];

const MODULE_TILE_STYLE: CSSProperties = {
  minHeight: 170,
  padding: 24,
  borderRadius: 12,
  borderWidth: 0.5,
};

const MODULE_ILLUSTRATION_STYLE: CSSProperties = {
  width: 56,
  height: 56,
  marginBottom: 16,
  flexShrink: 0,
};

const MODULE_SOON_BADGE_STYLE: CSSProperties = {
  position: "absolute",
  top: 16,
  right: 16,
  fontSize: 11,
};

const MODULE_ARROW_STYLE: CSSProperties = {
  alignSelf: "flex-end",
  marginTop: 12,
  color: "#c7541f",
  position: "static",
  right: "auto",
  bottom: "auto",
};

function ModuleTileIllustration({
  id,
  isDark,
}: {
  id: ModuleTileId;
  isDark: boolean;
}) {
  const illustrationProps = {
    isDark,
    className: "dashboard-module-illustration",
    style: MODULE_ILLUSTRATION_STYLE,
    "aria-hidden": true as const,
  };

  switch (id) {
    case "oferty":
      return <OfertyIllustration {...illustrationProps} />;
    case "umowy":
      return <UmowyIllustration {...illustrationProps} />;
    case "harmonogramy":
      return <HarmonogramyIllustration {...illustrationProps} />;
    case "koszty":
      return <KosztyIllustration {...illustrationProps} />;
  }
}

function getUserFirstName(user: User | null | undefined): string {
  const fullName =
    typeof user?.user_metadata?.full_name === "string"
      ? user.user_metadata.full_name.trim()
      : "";
  if (fullName) {
    const first = fullName.split(/\s+/)[0];
    if (first) return first;
  }
  return displayFirstName(user?.email);
}

export default function DashboardStartPage() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const firstName = getUserFirstName(user);

  useEffect(() => {
    if (searchParams.get("checkout") !== "success") return;
    trackEvent("checkout_completed");
    toast({
      title: "Subskrypcja aktywna!",
      description: "Limit 3 ofert/mies został zdjęty. Faktura przyjdzie e-mailem.",
    });
    const next = new URLSearchParams(searchParams);
    next.delete("checkout");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, toast]);

  return (
    <DashboardLayout>
      <header className="dashboard-start-header">
        <h1 className="dashboard-greeting">Dzień dobry, {firstName}</h1>
        <p className="dashboard-subtitle">Co chcesz dziś zrobić?</p>
      </header>

      <div className="dashboard-module-grid">
        {MODULE_TILES.map((tile) => {
          const content = (
            <>
              {tile.locked && (
                <span className="dashboard-module-soon" style={MODULE_SOON_BADGE_STYLE}>
                  Wkrótce
                </span>
              )}
              <ModuleTileIllustration id={tile.id} isDark={isDark} />
              <div className="dashboard-module-body">
                <h2 className="dashboard-module-title">{tile.title}</h2>
                <p className="dashboard-module-desc">{tile.description}</p>
                {!tile.locked && (
                  <span className="dashboard-module-arrow" style={MODULE_ARROW_STYLE} aria-hidden>
                    →
                  </span>
                )}
              </div>
            </>
          );

          if (tile.locked) {
            return (
              <div
                key={tile.id}
                className="dashboard-module-tile is-locked"
                style={{
                  ...MODULE_TILE_STYLE,
                  opacity: 0.65,
                  cursor: "not-allowed",
                }}
                aria-disabled
              >
                {content}
              </div>
            );
          }

          return (
            <button
              key={tile.id}
              type="button"
              className="dashboard-module-tile is-active"
              style={{
                ...MODULE_TILE_STYLE,
                borderColor: "#c7541f",
              }}
              onClick={() => navigate("/dashboard/oferty")}
            >
              {content}
            </button>
          );
        })}
      </div>

    </DashboardLayout>
  );
}
